import type { ComponentChildren } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { Icon } from "./icons";

/** The side swiped open: "star" lies under the left of the row (a swipe right), "delete" under the right. */
export type SwipeSide = "star" | "delete";

/** Width of an action left open, and how far the row must be dragged to leave it open. */
const ACTION_W = 88;
const OPEN_AT = ACTION_W / 2;
/** Movement before a drag counts as sideways or as scrolling. */
const SLOP = 8;
/** A full swipe: this share of the row's width does the action on release. */
const FULL = 0.55;
/** The row snapping into place, sliding out and folding up (styles.css has the same). */
const SNAP_MS = 180;

interface Props {
  /** Swipes on: off for a message no longer in Yahoo, which has nothing to star or delete. */
  enabled: boolean;
  /** The side left open, by the list (one row at a time). */
  open: SwipeSide | null;
  flagged: boolean;
  onOpen: (side: SwipeSide | null) => void;
  onStar: () => void;
  onDelete: () => void;
  children: ComponentChildren;
}

/** A listing row with Yahoo's swipes: right to star (or remove the star), left to delete. Dragged part of the way and
 * let go, the action stays open to be tapped; dragged most of the way across, it is done on release. Touch only: a
 * drag that starts up or down scrolls the list as before, and a tap on the row opens the message (one on a row left
 * open closes it). The row follows the finger without a render: its place is set on the elements directly. */
export function SwipeRow({ enabled, open, flagged, onOpen, onStar, onDelete, children }: Props) {
  const box = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const drag = useRef<{ x: number; y: number; base: number; dx: number; axis: "x" | "y" | null } | null>(null);
  /** Where the row is: px to the right, negative to the left. */
  const at = useRef(0);
  /** The tap that ends a sideways drag opens nothing. */
  const swiped = useRef(false);
  const leaving = useRef(false);

  const rest = (side: SwipeSide | null) => (side === "star" ? ACTION_W : side === "delete" ? -ACTION_W : 0);

  /** The row at `x`, with the side it uncovers as wide as the gap: snapping there, or following the finger (`width`:
   * the row's, to tell when a release there does the action). */
  const place = (x: number, width?: number) => {
    const el = content.current;
    const outer = box.current;
    if (!el || !outer) return;
    at.current = x;
    outer.classList.toggle("dragging", width !== undefined);
    outer.dataset.side = x > 0 ? "star" : x < 0 ? "delete" : "";
    outer.style.setProperty("--reveal", `${Math.abs(x)}px`);
    el.style.transform = x ? `translateX(${x}px)` : "";
    const armed = width !== undefined && Math.abs(x) >= width * FULL;
    if (armed && !outer.classList.contains("armed")) navigator.vibrate?.(10);
    outer.classList.toggle("armed", armed);
  };

  // The list opens and closes rows (another row swiped or tapped): follow it.
  useEffect(() => {
    if (!drag.current && !leaving.current && at.current !== rest(open)) place(rest(open));
  }, [open]);

  const star = () => {
    place(0);
    onOpen(null);
    onStar();
  };
  /** Out to the left, then the row folds up and goes from the list. */
  const remove = () => {
    const outer = box.current;
    if (!outer || leaving.current) return;
    leaving.current = true;
    place(-outer.offsetWidth);
    setTimeout(() => {
      outer.style.height = `${outer.offsetHeight}px`;
      void outer.offsetHeight;
      outer.classList.add("folding");
      outer.style.height = "0px";
      setTimeout(onDelete, SNAP_MS);
    }, SNAP_MS);
  };

  const onTouchStart = (e: TouchEvent) => {
    swiped.current = false;
    if (!enabled || leaving.current || e.touches.length > 1) return;
    drag.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, base: at.current, dx: 0, axis: null };
  };
  const onTouchEnd = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || d.axis !== "x") return;
    const x = d.base + d.dx;
    const width = box.current?.offsetWidth ?? 0;
    if (x <= -width * FULL) remove();
    else if (x >= width * FULL) star();
    else {
      const side: SwipeSide | null = x <= -OPEN_AT ? "delete" : x >= OPEN_AT ? "star" : null;
      place(rest(side));
      onOpen(side);
    }
  };
  const onTouchCancel = () => {
    if (!drag.current) return;
    drag.current = null;
    place(rest(open));
  };

  // Not passive, so a sideways drag does not scroll the list as well. It reads refs only, so it is added once.
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      const d = drag.current;
      if (!d) return;
      const dx = e.touches[0].clientX - d.x;
      const dy = e.touches[0].clientY - d.y;
      if (d.axis === null) {
        if (Math.abs(dx) < SLOP && Math.abs(dy) < SLOP) return;
        d.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
        if (d.axis === "y") {
          drag.current = null;
          return;
        }
        swiped.current = true;
      }
      e.preventDefault();
      d.dx = dx;
      const width = el.offsetWidth;
      const x = d.base + dx;
      // past the row's edge it moves ever slower
      place(Math.abs(x) > width ? Math.sign(x) * (width + (Math.abs(x) - width) / 4) : x, width);
    };
    el.addEventListener("touchmove", onTouchMove, { passive: false });
    return () => el.removeEventListener("touchmove", onTouchMove);
  }, []);

  return (
    <div class="swipe" ref={box} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onTouchCancel={onTouchCancel}>
      {enabled && (
        <>
          <button class="swipe-action swipe-star" tabIndex={-1} onClick={star}>
            <span>
              <Icon name="star" filled={!flagged} />
              {flagged ? "Unstar" : "Star"}
            </span>
          </button>
          <button class="swipe-action swipe-delete" tabIndex={-1} onClick={remove}>
            <span>
              <Icon name="trash" />
              Delete
            </span>
          </button>
        </>
      )}
      <div
        class="swipe-content"
        ref={content}
        onClickCapture={(e) => {
          // the end of a sideways drag, or a tap on a row left open (which closes it): the message is not opened
          if (!swiped.current && !open) return;
          e.preventDefault();
          e.stopPropagation();
          swiped.current = false;
          if (open) onOpen(null);
        }}
      >
        {children}
      </div>
    </div>
  );
}
